// ═══════════════════════════════════════════════════════════════
// TRADING UTILITIES - TidalWave Pro
// ═══════════════════════════════════════════════════════════════

// Magic number for EA trades
export const MAGIC_NUMBER = 202402;

// Trading symbols
export const DESIRED_SYMBOLS = ["EURUSD", "GBPUSD", "USDJPY", "XAUUSD", "BTCUSD"];
export const DISPLAY_SYMBOLS = ["BTCUSD", "EURUSD", "GBPUSD", "USDJPY", "XAUUSD"];

// Minimum SL/TP distance in pips (broker requirement)
export const MIN_SL_TP_DISTANCE = {
  XAUUSD: 10,   // 10 pips minimum for Gold scalping
  EURUSD: 10,   // 10 pips minimum
  GBPUSD: 10,   // 10 pips minimum
  USDJPY: 10,   // 10 pips minimum
  BTCUSD: 50,   // 50 pips minimum for crypto
};

// Pip values per symbol
export const PIP_VALUES = {
  XAUUSD: 0.01,   // Gold: 1 pip = 0.01
  USDJPY: 0.001,  // JPY pairs: 1 pip = 0.001
  EURUSD: 0.00001, // Standard pairs: 1 pip = 0.00001
  GBPUSD: 0.00001,
  BTCUSD: 0.01,   // Crypto: 1 pip = 0.01
};

// Maximum risk per trade as percentage of account balance
export const MAX_RISK_PERCENT = 2; // 2% max risk per trade

// Possible real broker symbol names
export const POSSIBLE_REAL_SYMBOLS = {
  BTCUSD: ["BTCUSD", "BTCUSD.m", "BTCUSD.raw", "BTCUSD.ecn", "BTCUSD.pro"],
  EURUSD: ["EURUSD", "EURUSD.m", "EURUSD.raw", "EURUSD.ecn", "EURUSD.pro"],
  GBPUSD: ["GBPUSD", "GBPUSD.m", "GBPUSD.raw", "GBPUSD.ecn", "GBPUSD.pro"],
  USDJPY: ["USDJPY", "USDJPY.m", "USDJPY.raw", "USDJPY.ecn", "USDJPY.pro"],
  XAUUSD: ["XAUUSD", "XAUUSD.m", "XAUUSD.raw", "XAUUSD.ecn", "XAUUSD.pro", "GOLD"],
};

// Available trading bots
export const AVAILABLE_BOTS = [
  { id: "1", name: "Gold Scalper", symbol: "XAUUSD", description: "Fast scalping on Gold" },
  { id: "2", name: "EURUSD Scalper", symbol: "EURUSD", description: "Scalping on EURUSD" },
  { id: "3", name: "GBPUSD Scalper", symbol: "GBPUSD", description: "Scalping on GBPUSD" },
  { id: "4", name: "JPY Breakout Pro", symbol: "USDJPY", description: "Breakout on USDJPY" },
  { id: "5", name: "BTCUSD Scalper", symbol: "BTCUSD", description: "Breakout on BTCUSD" },
];

// ═══════════════════════════════════════════════════════════════
// TECHNICAL INDICATORS
// ═══════════════════════════════════════════════════════════════

// Calculate Simple Moving Average (SMA)
export const calculateSMA = (prices, period) => {
  if (prices.length < period) return null;
  const slice = prices.slice(-period);
  return slice.reduce((a, b) => a + b, 0) / period;
};

// Calculate Exponential Moving Average (EMA)
export const calculateEMA = (prices, period) => {
  if (prices.length < period) return null;
  const multiplier = 2 / (period + 1);
  let ema = prices.slice(0, period).reduce((a, b) => a + b, 0) / period;
  for (let i = period; i < prices.length; i++) {
    ema = (prices[i] - ema) * multiplier + ema;
  }
  return ema;
};

// Calculate RSI (Relative Strength Index)
export const calculateRSI = (prices, period = 14) => {
  if (prices.length < period + 1) return null;
  let gains = 0;
  let losses = 0;
  for (let i = 1; i <= period; i++) {
    const change = prices[i] - prices[i - 1];
    if (change > 0) gains += change;
    else losses -= change;
  }
  const avgGain = gains / period;
  const avgLoss = losses / period;
  if (avgLoss === 0) return 100;
  const rs = avgGain / avgLoss;
  return 100 - 100 / (1 + rs);
};

// Calculate ATR (Average True Range)
export const calculateATR = (prices, period = 14) => {
  if (prices.length < period + 1) return null;
  const trs = [];
  for (let i = 1; i < prices.length; i++) {
    const high = prices[i].high;
    const low = prices[i].low;
    const prevClose = prices[i - 1].close;
    const tr = Math.max(
      high - low,
      Math.abs(high - prevClose),
      Math.abs(low - prevClose)
    );
    trs.push(tr);
  }
  return trs.slice(-period).reduce((a, b) => a + b, 0) / period;
};

// Calculate Standard Deviation for volatility
export const calculateStdDev = (prices) => {
  if (prices.length < 2) return null;
  const mean = prices.reduce((a, b) => a + b, 0) / prices.length;
  const variance = prices.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / prices.length;
  return Math.sqrt(variance);
};

// ═══════════════════════════════════════════════════════════════
// MARKET SESSION UTILITIES
// ═══════════════════════════════════════════════════════════════

// Get current market session
export const getMarketSession = () => {
  const now = new Date();
  const hour = now.getUTCHours();
  const day = now.getUTCDay();

  // Weekend
  if (day === 0 || day === 6) return { name: "Weekend", isActive: false };

  // Sydney: 22:00 - 07:00 UTC
  if (hour >= 22 || hour < 7) return { name: "Sydney", isActive: true };
  // Tokyo: 00:00 - 09:00 UTC
  if (hour >= 0 || hour < 9) return { name: "Tokyo", isActive: true };
  // London: 08:00 - 17:00 UTC
  if (hour >= 8 && hour < 17) return { name: "London", isActive: true };
  // New York: 13:00 - 22:00 UTC
  if (hour >= 13 && hour < 22) return { name: "New York", isActive: true };

  return { name: "Off Hours", isActive: false };
};

// Check if symbol is in a valid market session
export const isValidTradingSession = (symbol) => {
  const hour = new Date().getUTCHours();
  const day = new Date().getUTCDay();

  // Weekend - only crypto trades
  if (day === 0 || day === 6) {
    return symbol === "BTCUSD";
  }

  // Major pairs
  if (["EURUSD", "GBPUSD", "USDJPY"].includes(symbol)) {
    return hour >= 8 && hour < 22;
  }

  // Gold trades almost anytime except weekend
  if (symbol === "XAUUSD") {
    return hour >= 1 && hour < 23;
  }

  // BTCUSD trades 24/7
  if (symbol === "BTCUSD") {
    return true;
  }

  return false;
};

// Check correlation with existing positions
export const checkCorrelation = (symbol, openPositions) => {
  const correlations = {
    EURUSD: ["GBPUSD"],
    GBPUSD: ["EURUSD"],
    USDJPY: ["USDJPY"],
    XAUUSD: ["BTCUSD"],
    BTCUSD: ["XAUUSD"],
  };

  const correlatedPairs = correlations[symbol] || [];

  for (const [openSymbol, positions] of Object.entries(openPositions)) {
    if (positions && positions.length > 0) {
      if (openSymbol === symbol && positions.length >= 2) {
        return { allowed: false, reason: `Max positions on ${symbol} reached` };
      }
      if (correlatedPairs.includes(openSymbol) && positions.length >= 1) {
        return { allowed: false, reason: `Correlated position already open on ${openSymbol}` };
      }
    }
  }

  return { allowed: true, reason: "No correlation conflict" };
};

// Calculate % change
export const calculateChange = (oldPrice, newPrice) => {
  if (oldPrice === undefined || newPrice === undefined || oldPrice === 0) return "—";
  const diff = ((newPrice - oldPrice) / oldPrice) * 100;
  const sign = diff >= 0 ? "+" : "";
  return `${sign}${diff.toFixed(2)}%`;
};

// Format price based on symbol
export const formatPrice = (price, symbol) => {
  const isJpy = symbol.toUpperCase().includes("JPY");
  const isGold = symbol === "XAUUSD";
  
  if (isGold) return price.toFixed(2);
  if (isJpy) return price.toFixed(3);
  return price.toFixed(5);
};

// ═══════════════════════════════════════════════════════════════
// SL/TP VALIDATION FUNCTIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Calculate proper SL/TP levels based on USD values
 * @param {string} symbol - Trading symbol
 * @param {string} side - 'BUY' or 'SELL'
 * @param {number} entryPrice - Entry price of the trade
 * @param {number} volume - Position size in lots
 * @param {number} slUSD - Stop loss in USD
 * @param {number} tpUSD - Take profit in USD
 * @returns {object} - { stopLoss, takeProfit, slPips, tpPips }
 */
export const calculateSLTPFromUSD = (symbol, side, entryPrice, volume, slUSD, tpUSD) => {
  const isXAU = symbol.includes('XAU') || symbol.includes('GOLD');
  const isJPY = symbol.includes('JPY');
  
  // Pip value in price terms
  const pip = isXAU ? 0.01 : isJPY ? 0.001 : 0.00001;
  
  // Value per pip per lot
  let valuePerPip;
  if (isXAU) {
    valuePerPip = 100; // $100 per pip per lot for XAUUSD
  } else if (isJPY) {
    valuePerPip = 1000; // ~$1000 per pip per lot for JPY pairs
  } else {
    valuePerPip = 10; // $10 per pip per lot for standard forex
  }
  
  // Calculate pips needed for SL/TP
  const slPips = slUSD / (volume * valuePerPip);
  const tpPips = tpUSD / (volume * valuePerPip);
  
  let stopLoss, takeProfit;
  
  if (side === 'BUY') {
    stopLoss = entryPrice - (slPips * pip);
    takeProfit = entryPrice + (tpPips * pip);
  } else { // SELL
    stopLoss = entryPrice + (slPips * pip);
    takeProfit = entryPrice - (tpPips * pip);
  }
  
  return { stopLoss, takeProfit, slPips, tpPips };
};

/**
 * Validate that SL/TP distance meets minimum requirements
 * @param {string} symbol - Trading symbol
 * @param {number} entryPrice - Entry price of the trade
 * @param {number|null} stopLoss - Stop loss price
 * @param {number|null} takeProfit - Take profit price
 * @param {string} side - 'BUY' or 'SELL'
 * @returns {object} - { valid: boolean, errors: string[] }
 */
export const validateSLTPDistance = (symbol, entryPrice, stopLoss, takeProfit, side) => {
  const errors = [];
  const minPips = MIN_SL_TP_DISTANCE[symbol] || 20;
  const pipValue = PIP_VALUES[symbol] || 0.00001;
  const minDistance = minPips * pipValue;

  if (stopLoss) {
    const slDistance = side === 'BUY' 
      ? entryPrice - stopLoss 
      : stopLoss - entryPrice;
    
    if (slDistance < minDistance) {
      errors.push(`Stop Loss is too close (${(slDistance / pipValue).toFixed(1)} pips). Minimum: ${minPips} pips`);
    }
  }

  if (takeProfit) {
    const tpDistance = side === 'BUY' 
      ? takeProfit - entryPrice 
      : entryPrice - takeProfit;
    
    if (tpDistance < minDistance) {
      errors.push(`Take Profit is too close (${(tpDistance / pipValue).toFixed(1)} pips). Minimum: ${minPips} pips`);
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Validate that SL/TP values don't exceed maximum risk
 * @param {string} symbol - Trading symbol
 * @param {number} volume - Position size in lots
 * @param {number} entryPrice - Entry price
 * @param {number|null} stopLoss - Stop loss price
 * @param {number} accountBalance - Account balance in USD
 * @returns {object} - { valid: boolean, riskPercent: number, errors: string[] }
 */
export const validateMaxRisk = (symbol, volume, entryPrice, stopLoss, accountBalance) => {
  const errors = [];
  
  if (!stopLoss || !accountBalance || accountBalance <= 0) {
    return { valid: true, riskPercent: 0, errors: [] };
  }

  const pipValue = PIP_VALUES[symbol] || 0.00001;
  
  // Calculate SL distance in pips
  const slDistance = Math.abs(entryPrice - stopLoss) / pipValue;
  
  // Calculate approximate dollar risk per pip
  // Standard lot = 100,000 units
  const lotUnits = volume * 100000;
  const dollarPerPip = (pipValue * lotUnits);
  
  const potentialLoss = slDistance * dollarPerPip;
  const riskPercent = (potentialLoss / accountBalance) * 100;

  if (riskPercent > MAX_RISK_PERCENT) {
    errors.push(`Risk ${riskPercent.toFixed(2)}% exceeds maximum ${MAX_RISK_PERCENT}%. Reduce lot size or SL distance.`);
  }

  return {
    valid: riskPercent <= MAX_RISK_PERCENT,
    riskPercent: parseFloat(riskPercent.toFixed(2)),
    potentialLoss: parseFloat(potentialLoss.toFixed(2)),
    errors
  };
};

/**
 * Validate SL/TP configuration for a trade
 * @param {object} trade - Trade configuration
 * @returns {object} - Validation result with all issues
 */
export const validateTradeSLTP = (trade) => {
  const { symbol, entryPrice, stopLoss, takeProfit, side, volume, accountBalance } = trade;
  const allErrors = [];

  // Validate SL/TP direction correctness
  if (stopLoss && takeProfit) {
    if (side === 'BUY') {
      if (stopLoss >= entryPrice) {
        allErrors.push('Stop Loss must be below entry for BUY positions');
      }
      if (takeProfit <= entryPrice) {
        allErrors.push('Take Profit must be above entry for BUY positions');
      }
      if (stopLoss >= takeProfit) {
        allErrors.push('Stop Loss must be below Take Profit for BUY positions');
      }
    } else if (side === 'SELL') {
      if (stopLoss <= entryPrice) {
        allErrors.push('Stop Loss must be above entry for SELL positions');
      }
      if (takeProfit >= entryPrice) {
        allErrors.push('Take Profit must be below entry for SELL positions');
      }
      if (stopLoss <= takeProfit) {
        allErrors.push('Stop Loss must be above Take Profit for SELL positions');
      }
    }
  }

  // Validate minimum distance
  const distanceResult = validateSLTPDistance(symbol, entryPrice, stopLoss, takeProfit, side);
  allErrors.push(...distanceResult.errors);

  // Validate maximum risk
  const riskResult = validateMaxRisk(symbol, volume, entryPrice, stopLoss, accountBalance);
  allErrors.push(...riskResult.errors);

  return {
    valid: allErrors.length === 0,
    errors: allErrors,
    distanceValid: distanceResult.valid,
    riskValid: riskResult.valid,
    riskPercent: riskResult.riskPercent
  };
};
